import { TITRATION_PERMISSION } from "@/config/permission-keys";
import { usePatientVitallUserContext } from "@/contexts";
import { FhirResourceType, extractFhirResourceId } from "@/utils";
import { GET_USER_FEATURES } from "@/utils/GraphQLClient";
import { GetUserFeaturesData, GetUserInput } from "@/utils/GraphQLClient";

import * as Sentry from "@sentry/react";
import { useQuery } from "urql";

/**
 * Check if titration is enabled for the provided FHIR patient ID
 * @returns An object containing info about its loading and enabled statuses
 */
export const useGdmtEnabled = (
  patient_id: string
): {
  enabled: boolean;
  loading: boolean;
} => {
  const { fhirPatientIdToUserIdMap, isLoading: loadingOrgPatientUsers } = usePatientVitallUserContext();

  // The map is keyed by bare FHIR Patient resource IDs (e.g. "123"). Callers
  // pass `patient.id`, which is already bare, but fall back to stripping a
  // "Patient/123" reference defensively so either form resolves correctly.
  const fhir_patient_id = extractFhirResourceId(patient_id, FhirResourceType.Patient) ?? patient_id;
  const user_id = fhirPatientIdToUserIdMap.get(fhir_patient_id);

  const [{ data, fetching, error }] = useQuery<GetUserFeaturesData, { input: GetUserInput }>({
    query: GET_USER_FEATURES,
    variables: {
      input: {
        user_id: user_id ?? "",
      },
    },
    pause: !user_id || loadingOrgPatientUsers,
  });

  if (error) {
    Sentry.captureException("Titration: patient getUserFeatures query error", {
      level: "error",
      tags: { component: "useGdmtEnabled", feature: "titration", operation: "GET_USER_FEATURES" },
      extra: { error: JSON.stringify(error.graphQLErrors) },
    });
    return {
      enabled: false,
      loading: false,
    };
  }

  const enabled = data?.userFeatures.featuresv2.includes(TITRATION_PERMISSION);

  return {
    enabled: Boolean(enabled),
    loading: loadingOrgPatientUsers || fetching,
  };
};
